import React, { useState, useEffect } from "react";
import { X, Search, ChevronDown, Check } from "lucide-react";
import { masterService } from "../../api/masterService";
import SearchableSelect from "../SearchableSelect";

const STATUS_OPTIONS = [
  { id: "AVAILABLE", label: "Kosong", activeClass: "bg-emerald-100 border-emerald-400 text-emerald-700", baseClass: "border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/50" },
  { id: "OCCUPIED", label: "Terisi", activeClass: "bg-rose-100 border-rose-400 text-rose-700", baseClass: "border-slate-200 text-slate-600 hover:border-rose-300 hover:bg-rose-50/50" },
  { id: "RESERVED", label: "Reservasi", activeClass: "bg-amber-100 border-amber-400 text-amber-700", baseClass: "border-slate-200 text-slate-600 hover:border-amber-300 hover:bg-amber-50/50" },
];

const normalizeStatus = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "OCCUPIED" || s === "TERISI") return "OCCUPIED";
  if (s === "RESERVED" || s === "RESERVASI") return "RESERVED";
  if (s === "INACTIVE" || s === "NONAKTIF") return "INACTIVE";
  return "AVAILABLE";
};

export default function SelectTableModal({
  isOpen,
  onClose,
  onSelectTable,
  selectedTable = null,
  customer = null,
  walkInName = "",
}) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [activeTableId, setActiveTableId] = useState(selectedTable?.id || null);
  const [statusModalTable, setStatusModalTable] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "AVAILABLE", reserved_by: "", notes: "" });
  const [savingStatus, setSavingStatus] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchTables();
      setActiveTableId(selectedTable?.id || null);
    }
  }, [isOpen, selectedTable]);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const res = await masterService.tables.list({ limit: 100 });
      const list = res?.data ?? res ?? [];
      setTables(Array.isArray(list) ? list : list?.data || []);
    } catch (err) {
      console.error("Gagal memuat data meja:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getActiveCustomerName = () =>
    customer?.name || (walkInName && walkInName.trim()) || "Pelanggan Umum";

  const getStatusTime = () => {
    const time = new Date().toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    }).replace(".", ":");
    return `Hari ini, ${time}`;
  };

  const getStatusBadgeClass = (table) => {
    const status = normalizeStatus(table.status);
    if (status === "OCCUPIED") return "bg-rose-100 text-rose-700";
    if (status === "RESERVED") return "bg-amber-100 text-amber-700";
    if (status === "INACTIVE") return "bg-slate-100 text-slate-500";
    return "bg-emerald-100 text-emerald-700";
  };

  const getStatusLabel = (table) => {
    const status = normalizeStatus(table.status);
    if (status === "OCCUPIED") return "Terisi";
    if (status === "RESERVED") return "Reservasi";
    if (status === "INACTIVE") return "Nonaktif";
    return "Kosong";
  };

  const openStatusModal = (e, table) => {
    e.stopPropagation();
    if (normalizeStatus(table.status) === "INACTIVE") return;
    setStatusModalTable(table);
    setStatusForm({
      status: normalizeStatus(table.status),
      reserved_by: table.reserved_by || "",
      notes: table.notes || "",
    });
  };

  const closeStatusModal = () => {
    if (savingStatus) return;
    setStatusModalTable(null);
  };

  const handlePickStatus = (newStatus) => {
    setStatusForm((prev) => {
      if (newStatus === "AVAILABLE") {
        return { status: newStatus, reserved_by: "", notes: "" };
      }
      return {
        status: newStatus,
        reserved_by: prev.reserved_by || getActiveCustomerName(),
        notes: prev.notes || getStatusTime(),
      };
    });
  };

  const saveStatusForm = async () => {
    if (!statusModalTable || savingStatus) return;
    setSavingStatus(true);
    try {
      await masterService.tables.updateStatus(statusModalTable.id, {
        status: statusForm.status,
        notes: statusForm.notes,
        reserved_by: statusForm.reserved_by,
      });
      setTables((prev) =>
        prev.map((t) =>
          t.id === statusModalTable.id
            ? {
                ...t,
                status: statusForm.status,
                notes: statusForm.notes,
                reserved_by: statusForm.reserved_by,
              }
            : t
        )
      );
      setStatusModalTable(null);
    } catch (err) {
      console.error("Gagal menyimpan status meja:", err);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleConfirmSelect = async () => {
    const chosen = tables.find((t) => t.id === activeTableId);
    if (!chosen) {
      onSelectTable(null);
      onClose();
      return;
    }

    // Meja kosong dipilih lalu simpan -> otomatis ubah status jadi Terisi
    // dengan nama pelanggan dan waktu saat itu, tanpa ubah manual dulu
    if (normalizeStatus(chosen.status) === "AVAILABLE") {
      const customerName = getActiveCustomerName();
      const timeNote = getStatusTime();
      setSavingStatus(true);
      try {
        await masterService.tables.updateStatus(chosen.id, {
          status: "OCCUPIED",
          notes: timeNote,
          reserved_by: customerName,
        });
        const updatedTable = {
          ...chosen,
          status: "OCCUPIED",
          notes: timeNote,
          reserved_by: customerName,
        };
        setTables((prev) =>
          prev.map((t) => (t.id === chosen.id ? updatedTable : t))
        );
        setWarningMessage("");
        onSelectTable(updatedTable);
      } catch (err) {
        console.error("Gagal otomatis mengisi status meja:", err);
        setWarningMessage("Gagal mengubah status meja menjadi Terisi, coba lagi");
        return;
      } finally {
        setSavingStatus(false);
      }
    } else {
      onSelectTable(chosen);
    }
    onClose();
  };

  const [page, setPage] = useState(1);
  const itemsPerPage = 8;

  // Reset page when filter or search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  // Filtering
  const filteredTables = tables.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.notes && t.notes.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.reserved_by && t.reserved_by.toLowerCase().includes(searchQuery.toLowerCase()));

    const status = normalizeStatus(t.status);
    if (statusFilter === "ALL") return matchesSearch;
    if (statusFilter === "INACTIVE") return matchesSearch && status === "INACTIVE";
    return matchesSearch && status === statusFilter;
  });

  const totalEntries = filteredTables.length;
  const totalPages = Math.ceil(totalEntries / itemsPerPage) || 1;
  const paginatedTables = filteredTables.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  const startEntry = totalEntries === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const endEntry = Math.min(page * itemsPerPage, totalEntries);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-4xl w-full p-6 shadow-2xl space-y-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="font-bold text-slate-900 text-xl">Pilih Meja</h3>
            <p className="text-xs text-slate-500 mt-0.5">Pilih meja untuk pesanan dine in</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning Message */}
        {warningMessage && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-3.5 py-2.5 text-xs font-medium animate-in fade-in duration-150">
            <span className="mt-px shrink-0">⚠</span>
            <span>{warningMessage}</span>
          </div>
        )}

        {/* Controls Bar: Search & Status Filter */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nomor meja..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all bg-slate-50/50 focus:bg-white"
            />
          </div>

          {/* Status Filter Dropdown */}
          <div className="w-full sm:w-48">
            <SearchableSelect
              options={[
                { id: "ALL", name: "Semua Status" },
                { id: "AVAILABLE", name: "Kosong" },
                { id: "OCCUPIED", name: "Terisi" },
                { id: "RESERVED", name: "Reservasi" },
                { id: "INACTIVE", name: "Nonaktif" },
              ]}
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              placeholder="Semua Status"
              searchable={false}
              size="md"
            />
          </div>
        </div>

        {/* Hint */}
        <p className="text-[11px] text-slate-400 -mt-3">
          Klik badge status pada kartu meja untuk mengubah status atau catatan meja
        </p>

        {/* Grid Cards (8 per page) */}
        <div className="min-h-[340px] pr-1">
          {loading ? (
            <div className="py-16 text-center text-xs text-slate-400">
              Memuat daftar meja...
            </div>
          ) : filteredTables.length === 0 ? (
            <div className="py-16 text-center text-xs text-slate-400">
              Tidak ada meja yang sesuai kriteria pencarian/filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {paginatedTables.map((table) => {
                const isSelected = activeTableId === table.id;
                const status = normalizeStatus(table.status);
                const isReserved = status === "RESERVED";
                const isOccupied = status === "OCCUPIED";

                // Background & Border styling according to status
                let cardBgClass = "bg-white border-slate-200 hover:border-teal-400";
                if (isReserved) cardBgClass = "bg-amber-50/40 border-amber-200 hover:border-amber-400";
                if (isOccupied) cardBgClass = "bg-rose-50/30 border-rose-200 hover:border-rose-400";
                return (
                  <div
                    key={table.id}
                    onClick={() => {
                      setActiveTableId(table.id);
                      setWarningMessage("");
                    }}
                    className={`rounded-2xl border p-3.5 transition-all duration-200 cursor-pointer flex flex-col justify-between space-y-2.5 relative ${cardBgClass}`}>
                    {isSelected && (
                      <div
                        aria-hidden="true"
                        className="absolute inset-1 rounded-[14px] border-2 border-teal-600 pointer-events-none"
                      />
                    )}

                    {/* Top Row: Table Name + Status Badge */}
                    <div className="flex items-center justify-between">
                      <h4 className="font-extrabold text-slate-900 text-base flex items-center gap-1.5">
                        <span>{table.name}</span>
                        {isSelected && (
                          <span className="bg-teal-600 text-white p-0.5 rounded-full inline-flex items-center justify-center shrink-0" title="Meja Terpilih">
                            <Check className="w-3 h-3 stroke-[3]" />
                          </span>
                        )}
                      </h4>

                      {status === "INACTIVE" ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${getStatusBadgeClass(table)}`}>
                          Nonaktif
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => openStatusModal(e, table)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors hover:brightness-95 cursor-pointer ${getStatusBadgeClass(table)}`}
                          title="Klik untuk ubah status atau catatan">
                          <span>{getStatusLabel(table)}</span>
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Capacity */}
                    <p className="text-xs text-slate-500 font-medium">
                      Kapasitas: <span className="font-semibold text-slate-700">{table.capacity || 2} Orang</span>
                    </p>

                    {/* Info / Note Box Section */}
                    <div className="pt-2 border-t border-slate-100/80">
                      {isOccupied || isReserved ? (
                        <div
                          onClick={(e) => openStatusModal(e, table)}
                          className={`rounded-xl p-2.5 text-xs space-y-0.5 min-h-[58px] cursor-pointer transition-colors ${
                            isReserved
                              ? "bg-amber-50/70 border border-amber-200/80 hover:bg-amber-100/70"
                              : "bg-rose-50/70 border border-rose-200/80 hover:bg-rose-100/70"
                          }`}
                          title="Klik untuk ubah status atau catatan">
                          <div className="text-slate-500 text-[11px]">
                            {isReserved ? "Reservasi oleh" : "Terisi oleh"}
                          </div>
                          <p className="font-bold text-slate-900 text-xs truncate">
                            {table.reserved_by || "Pelanggan Umum"}
                          </p>
                          <p className="text-[11px] text-slate-600 font-medium truncate">
                            {table.notes || getStatusTime()}
                          </p>
                        </div>
                      ) : (
                        <div
                          onClick={(e) => openStatusModal(e, table)}
                          className="bg-slate-50/60 border border-slate-200/80 rounded-xl p-2.5 text-xs text-slate-400 flex items-center justify-between min-h-[58px] cursor-pointer transition-colors hover:bg-slate-100/70"
                          title="Klik untuk tambah atau ubah catatan">
                          <span className="text-[11px] text-slate-400 leading-snug truncate">
                            {table.notes || "Tambahkan catatan (opsional)"}
                          </span>
                          <span className="text-[10px] font-semibold text-teal-600 ml-2 shrink-0">Ubah</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-3 pb-1 border-t border-slate-100 text-xs">
            <span className="text-slate-500 font-medium">
              Menampilkan <span className="font-semibold text-slate-800">{startEntry}-{endEntry}</span> dari <span className="font-semibold text-slate-800">{totalEntries}</span> meja
            </span>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors">
                ‹ Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-bold transition-colors ${
                    page === p
                      ? "bg-teal-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}>
                  {p}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors">
                Next ›
              </button>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl border border-teal-600 text-teal-700 hover:bg-teal-50 text-sm font-bold transition-colors text-center">
            Batal
          </button>
          <button
            type="button"
            onClick={handleConfirmSelect}
            disabled={savingStatus}
            className="w-full py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-bold transition-colors shadow-md text-center disabled:opacity-60">
            {savingStatus ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>

      {/* Popup Dalam Popup : Ubah Status & Catatan */}
      {statusModalTable && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeStatusModal}>
          <div
            className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}>
            {/* Header Popup */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900 text-base">Ubah Status Meja</h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  {statusModalTable.name} · {statusModalTable.capacity || 2} Orang
                </p>
              </div>
              <button
                onClick={closeStatusModal}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Pilihan Status */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Status Meja</label>
              <div className="grid grid-cols-3 gap-2">
                {STATUS_OPTIONS.map((opt) => {
                  const active = statusForm.status === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => handlePickStatus(opt.id)}
                      className={`px-2 py-2 rounded-xl border text-xs font-bold transition-all ${
                        active ? opt.activeClass : opt.baseClass
                      }`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {statusForm.status !== "AVAILABLE" && (
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Atas nama pelanggan dan catatan waktu akan terisi otomatis, bisa diubah
                </p>
              )}
            </div>

            {/* Atas Nama (RESERVED & OCCUPIED) */}
            {statusForm.status !== "AVAILABLE" && (
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${
                  statusForm.status === "RESERVED" ? "text-amber-700" : "text-rose-700"
                }`}>
                  {statusForm.status === "RESERVED" ? "Atas Nama (Pemesan Reservasi)" : "Terisi oleh (Nama Pelanggan)"}
                </label>
                <input
                  type="text"
                  value={statusForm.reserved_by}
                  onChange={(e) => setStatusForm({ ...statusForm, reserved_by: e.target.value })}
                  placeholder={statusForm.status === "RESERVED" ? "Contoh: Rudi Setiawan / Ibu Rani" : "Contoh: Eva, Budi, dll"}
                  className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                    statusForm.status === "RESERVED"
                      ? "bg-amber-50 border border-amber-200 text-amber-900 placeholder-amber-400 focus:ring-amber-500"
                      : "bg-rose-50 border border-rose-200 text-rose-900 placeholder-rose-400 focus:ring-rose-500"
                  }`}
                />
              </div>
            )}

            {/* Catatan */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Catatan / Keterangan <span className="text-slate-400">(Opsional)</span>
              </label>
              <textarea
                rows={2}
                value={statusForm.notes}
                onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                placeholder="Contoh: Hari ini, 19:30"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors resize-none"
              />
            </div>

            {/* Footer Popup */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={closeStatusModal}
                disabled={savingStatus}
                className="flex-1 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-bold transition-colors disabled:opacity-50">
                Batal
              </button>
              <button
                type="button"
                onClick={saveStatusForm}
                disabled={savingStatus}
                className="flex-1 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold transition-colors shadow-md disabled:opacity-50">
                {savingStatus ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
